import { Classes, Hotkey, Hotkeys, HotkeysTarget, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import $ from 'jquery'
import _ from 'lodash'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { SheetManager } from 'browser/components/atomic-elements/atoms/sheet/sheet-manager'
import 'browser/components/atomic-elements/atoms/expandable-panel/_expandable-panel.scss'

enum PanelMode {
  Minimized = 'minimized',
  Normal = 'normal',
  Expanded = 'expanded',
}

/**
 * @uiComponent
 */
interface IExpandablePanelProps extends IBaseProps {
  bodyClassName?: string
  headerClassName?: string
  mode?: PanelMode
  isMinimized?: boolean
  onClose: () => void
  panelFooter?: React.ReactElement<any>
  paperLevel?: string
  size?: string
  title: string
}

interface IExpandablePanelState {
  hasSheet: boolean
  mode: PanelMode
}

/**
 * A floating, dismissible panel (title, body, optional footer) that toggles between three
 * sizes — minimized, normal, and expanded — for overlaying detail/edit content on a page.
 */
@HotkeysTarget
export class ExpandablePanel extends React.Component<IExpandablePanelProps, IExpandablePanelState, never> {

  public static defaultProps: Partial<IExpandablePanelProps> = {
    isMinimized: false,
    mode: PanelMode.Normal,
    onClose: _.noop,
    paperLevel: '4',
  }

  private panelRef: HTMLElement

  constructor(props) {
    super(props)
    this.state = {
      hasSheet: false,
      mode: props.isMinimized ? PanelMode.Minimized : props.mode
    }
  }

  public componentDidMount() {
    this.focusPanel()
  }

  public render() {
    const { bodyClassName, children, className, panelFooter, paperLevel, size, title } = this.props
    const { mode } = this.state
    const isMinimized = mode === PanelMode.Minimized
    const isExpanded = mode === PanelMode.Expanded

    const maxHeight = $(window).height() - (50 + 90 + 14)
    const sizeClass = _.isEmpty(size) ? '' : `c-expandablePanel--${size}`
    const paperLevelClassName = `paper--zDepth-${paperLevel}`
    const panelMaxHeight = isExpanded ? undefined : maxHeight - 100

    const panel = (
      <SheetManager
        className={classNames('c-expandablePanel paper', paperLevelClassName, sizeClass, className, {
          'c-expandablePanel--isMinimized': isMinimized,
          'c-expandablePanel--expanded': isExpanded,
        })}
        onChange={this.handleSheetManagerChange}
        style={isExpanded ? { maxHeight: '80vh' } : { maxHeight: maxHeight + 'px' }}
      >
        {this.renderPanelHeader()}
        <div
          className={classNames('c-expandablePanelBody', bodyClassName, {
            'u-hide': isMinimized,
          })}
          style={panelMaxHeight != null ? { maxHeight: panelMaxHeight } : undefined}
        >
          {children}
        </div>
        {isMinimized ? null : panelFooter}
      </SheetManager>
    )

    return (
      <div
        className={classNames('c-expandablePanel-container', {
          'c-expandablePanel-container--expanded': isExpanded,
        })}
        data-debug-id={`expandablePanel:${title}`}
        tabIndex={-1}
        ref={this.handleSetPanelRef}
      >
        {isExpanded && (
          <div className='c-expandablePanel-backdrop' onClick={this.handleCollapse} />
        )}
        {panel}
      </div>
    )
  }

  public renderHotkeys() {
    const { hasSheet, mode } = this.state
    return (
      <Hotkeys>
        <Hotkey
          allowInInput={true}
          disabled={hasSheet}
          group='Panels'
          combo='esc'
          label='Close or Collapse Panel'
          onKeyDown={this.handleEsc}
          stopPropagation={true}
          preventDefault={true}
        />
      </Hotkeys>
    )
  }

  private renderPanelHeader() {
    const { title, headerClassName, onClose } = this.props
    const { mode } = this.state
    const isMinimized = mode === PanelMode.Minimized
    const isExpanded = mode === PanelMode.Expanded

    return (
      <div className={classNames('c-expandablePanelHeader', headerClassName)}>
        <div className='c-expandablePanelHeader-group' onClick={isMinimized ? this.handleRestore : undefined}>
          <div
            className={classNames('c-expandablePanelHeader-title', {
              'c-expandablePanelHeader-title--isMinimized': isMinimized,
            })}
          >
            {title}
          </div>
        </div>
        <div className='c-expandablePanelHeader-group'>
          {/* Minimize — shown in normal and expanded states */}
          <div
            className={classNames('c-expandablePanelHeader-item c-expandablePanelHeader-actionIcon', {
              'u-hide': isMinimized,
            })}
          >
            <Button onClick={this.handleMinimize} className={Classes.MINIMAL}>
              <Icon icon={IconNames.MINUS} />
            </Button>
          </div>
          {/* Restore — shown in minimized state */}
          <div
            className={classNames('c-expandablePanelHeader-item c-expandablePanelHeader-actionIcon', {
              'u-hide': !isMinimized,
            })}
          >
            <Button onClick={this.handleRestore} className={Classes.MINIMAL}>
              <Icon icon={IconNames.MAXIMIZE} />
            </Button>
          </div>
          {/* Expand — shown in normal state */}
          <div
            className={classNames('c-expandablePanelHeader-item c-expandablePanelHeader-actionIcon', {
              'u-hide': isMinimized || isExpanded,
            })}
          >
            <Button onClick={this.handleExpand} className={Classes.MINIMAL}>
              <Icon icon={IconNames.MAXIMIZE} />
            </Button>
          </div>
          {/* Collapse — shown in expanded state */}
          <div
            className={classNames('c-expandablePanelHeader-item c-expandablePanelHeader-actionIcon', {
              'u-hide': !isExpanded,
            })}
          >
            <Button onClick={this.handleCollapse} className={Classes.MINIMAL}>
              <Icon icon={IconNames.MINIMIZE} />
            </Button>
          </div>
          {/* Close — always visible */}
          <div className='c-expandablePanelHeader-item c-expandablePanelHeader-actionIcon'>
            <Button onClick={onClose} className={Classes.MINIMAL}>
              <Icon icon={IconNames.CROSS} />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  private focusPanel = () => {
    if (this.panelRef != null) {
      this.panelRef.focus()
    }
  }

  private handleSheetManagerChange = (sheets: any[]) => {
    const hasSheet = sheets.length > 0
    this.setState({ hasSheet })
    if (!hasSheet) {
      this.focusPanel()
    }
  }

  private handleSetPanelRef = (ref: Element) => {
    this.panelRef = ref as HTMLElement
  }

  private handleEsc = () => {
    const { mode } = this.state
    if (mode === PanelMode.Expanded) {
      this.handleCollapse()
    } else if (mode === PanelMode.Normal) {
      this.props.onClose()
    }
  }

  private setMode = (mode: PanelMode) => {
    this.setState({mode})
    if (mode !== PanelMode.Minimized) {
      // refocus the panel, since the click moved focus to the mode button
      setTimeout(this.focusPanel, 0)
    }
  }

  private handleMinimize = () => this.setMode(PanelMode.Minimized)
  private handleRestore  = () => this.setMode(PanelMode.Normal)
  private handleExpand   = () => this.setMode(PanelMode.Expanded)
  private handleCollapse = () => this.setMode(PanelMode.Normal)

}
